import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Modulo, Perfil } from '@scm/contratos';
import { api, ErrorApi } from '../lib/api.js';

/**
 * Sesion y permisos.
 *
 * Lo que decide quien ve que viene del SERVIDOR, no de una tabla en el
 * navegador: `/v1/sesion` devuelve el perfil, los permisos, `ve_montos` y
 * `ve_datos_personales`. Si
 * la interfaz llevara su propia copia, un permiso cambiado en Ajustes no
 * surtiria efecto hasta desplegar.
 */

interface Permiso {
  modulo: Modulo;
  acceso: 'ACCESO_LECTURA' | 'ACCESO_ESCRITURA';
}

export interface Usuario {
  id: string;
  nombre: string;
  perfil: Perfil;
  cliente_id: string | null;
  ve_montos: boolean;
  ve_datos_personales: boolean;
  permisos: Permiso[];
}

interface ContextoSesion {
  usuario: Usuario | null;
  cargando: boolean;
  entrar: (correo: string, contrasena: string, totp?: string) => Promise<void>;
  salir: () => Promise<void>;
  /** Si el perfil alcanza el modulo, para leer o para escribir. */
  puede: (modulo: Modulo, accion?: 'leer' | 'escribir') => boolean;
  /** Si el perfil recibe informacion economica. */
  veMontos: boolean;
  /** Si el perfil recibe datos personales: CURP, RFC, identificacion. */
  veDatosPersonales: boolean;
}

const Contexto = createContext<ContextoSesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }): JSX.Element {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [cargando, setCargando] = useState(true);

  const recargar = useCallback(async () => {
    try {
      const { datos } = await api.obtener<Usuario>('/v1/sesion');
      setUsuario(datos);
    } catch (error) {
      /* 401 al arrancar es lo normal: nadie ha entrado todavia. */
      if (!(error instanceof ErrorApi) || error.http !== 401) {
        console.error('No se pudo leer la sesion', error);
      }
      setUsuario(null);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const entrar = useCallback(
    async (correo: string, contrasena: string, totp?: string) => {
      await api.enviar('/v1/sesion', { correo, contrasena, ...(totp ? { totp } : {}) });
      await recargar();
    },
    [recargar],
  );

  const salir = useCallback(async () => {
    try {
      await api.quitar('/v1/sesion');
    } finally {
      /* Se limpia el estado local aunque el servidor falle: dejar la pantalla
         como si la sesion siguiera viva es peor que cerrarla de mas. */
      setUsuario(null);
    }
  }, []);

  const valor = useMemo<ContextoSesion>(
    () => ({
      usuario,
      cargando,
      entrar,
      salir,
      veMontos: usuario?.ve_montos ?? false,
      veDatosPersonales: usuario?.ve_datos_personales ?? false,
      puede: (modulo, accion = 'leer') => {
        const p = usuario?.permisos.find((x) => x.modulo === modulo);
        if (!p) return false;
        return accion === 'leer' || p.acceso === 'ACCESO_ESCRITURA';
      },
    }),
    [usuario, cargando, entrar, salir],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): ContextoSesion {
  const c = useContext(Contexto);
  if (!c) throw new Error('useSesion fuera del ProveedorSesion');
  return c;
}

/**
 * Filtrado de montos en la interfaz.
 *
 * Es cosmetica, y a proposito: el filtro de verdad vive en el serializador
 * del servidor, que ni siquiera manda la llave. Esto existe para que una
 * columna no quede como "—" sin explicacion cuando el perfil no alcanza.
 */
export function useMask(): (centavos: number | null | undefined, moneda?: string) => string {
  const { veMontos } = useSesion();
  return (centavos, moneda = 'MXN') => {
    if (!veMontos) return '—';
    if (centavos === null || centavos === undefined) return '—';
    return `$${new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(centavos / 100)} ${moneda}`;
  };
}
